import React, { useEffect, useState } from "react";
import axios from "axios";
import { base_url } from "../../utils/baseUrl";

const API_URL = `${base_url}sliders`;

export default function SliderManager() {
  const [sliders, setSliders] = useState([]);
  const [form, setForm] = useState({
    title: "",
    description: "",
    link: "",
    position: 0,
    isActive: true
  });
  const [image, setImage] = useState(null);
  const [editId, setEditId] = useState(null);

  // ✅ loaders
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchSliders();
  }, []);

  const fetchSliders = async () => {
    setLoading(true); // ✅
    const res = await axios.get(API_URL);
    setSliders(res.data.data);
    setLoading(false); // ✅
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true); // ✅

    const formData = new FormData();
    Object.keys(form).forEach((key) => formData.append(key, form[key]));
    if (image) formData.append("image", image);

    if (editId) {
      await axios.put(`${API_URL}/${editId}`, formData);
    } else {
      await axios.post(API_URL, formData);
    }

    resetForm();
    fetchSliders();
    setSubmitting(false); // ✅
  };

  const resetForm = () => {
    setForm({ title: "", description: "", link: "", position: 0, isActive: true });
    setImage(null);
    setEditId(null);
  };

  const handleEdit = (slider) => {
    setForm({
      title: slider.title,
      description: slider.description || "",
      link: slider.link || "",
      position: slider.position,
      isActive: slider.isActive
    });
    setEditId(slider._id);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this slider?")) {
      setDeletingId(id); // ✅
      await axios.delete(`${API_URL}/${id}`);
      fetchSliders();
      setDeletingId(null); // ✅
    }
  };

  return (
    <div className="container py-4">
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0">{editId ? "Update Slider" : "Create Slider"}</h5>
        </div>

        <div className="card-body">
          <form onSubmit={handleSubmit} encType="multipart/form-data">
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Title</label>
                <input
                  className="form-control"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Position</label>
                <input
                  type="number"
                  className="form-control"
                  name="position"
                  value={form.position}
                  onChange={handleChange}
                />
              </div>

              <div className="col-12">
                <label className="form-label">Description</label>
                <textarea
                  className="form-control"
                  name="description"
                  rows="3"
                  value={form.description}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Link</label>
                <input
                  className="form-control"
                  name="link"
                  value={form.link}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Slider Image</label>
                <input
                  type="file"
                  className="form-control"
                  onChange={(e) => setImage(e.target.files[0])}
                />
              </div>

              <div className="col-12">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    name="isActive"
                    checked={form.isActive}
                    onChange={handleChange}
                  />
                  <label className="form-check-label">Active</label>
                </div>
              </div>

              <div className="col-12 d-flex gap-2">
                <button type="submit" className="btn btn-success" disabled={submitting}>
                  {submitting && (
                    <span className="spinner-border spinner-border-sm me-2"></span>
                  )}
                  {editId ? "Update Slider" : "Create Slider"}
                </button>

                {editId && (
                  <button type="button" className="btn btn-secondary" onClick={resetForm}>
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-header bg-dark text-white">
          <h5 className="mb-0">Slider List</h5>
        </div>

        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Title</th>
                <th>Image</th>
                <th>Position</th>
                <th>Status</th>
                <th width="180">Actions</th>
              </tr>
            </thead>

            <tbody>
              {/* ✅ Main Loader */}
              {loading && (
                <tr>
                  <td colSpan="5" className="text-center py-5">
                    <div className="spinner-border text-primary"></div>
                  </td>
                </tr>
              )}

              {!loading &&
                sliders.map((s) => (
                  <tr key={s._id}>
                    <td>{s.title}</td>
                    <td>
                      <img src={s.image} alt="" width="90" className="rounded border" />
                    </td>
                    <td>{s.position}</td>
                    <td>
                      <span className={`badge ${s.isActive ? "bg-success" : "bg-secondary"}`}>
                        {s.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        <button className="btn btn-warning" onClick={() => handleEdit(s)}>
                          Edit
                        </button>
                        <button
                          className="btn btn-danger"
                          disabled={deletingId === s._id}
                          onClick={() => handleDelete(s._id)}
                        >
                          {deletingId === s._id && (
                            <span className="spinner-border spinner-border-sm me-1"></span>
                          )}
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

              {!loading && !sliders.length && (
                <tr>
                  <td colSpan="5" className="text-center text-muted py-4">
                    No sliders found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
